import React from 'react';
import { UseFormRegister, FieldErrors } from 'react-hook-form';

interface AddressFormProps {
  register: UseFormRegister<any>;
  errors?: FieldErrors;
  required?: boolean;
}

export const AddressForm: React.FC<AddressFormProps> = ({ register, errors, required = true }) => {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        {/* Street */}
        <div className="col-span-2">
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            Street {required && <span className="text-red-500">*</span>}
          </label>
          <input
            type="text"
            {...register('street', { required: required ? 'Street is required' : false })}
            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-shadow ${
              errors?.street ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="e.g. Warschauer Straße"
          />
          {errors?.street && (
            <p className="mt-1 text-sm text-red-500">{errors.street.message as string}</p>
          )}
        </div>

        {/* Street Number */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            Number {required && <span className="text-red-500">*</span>}
          </label>
          <input
            type="text"
            {...register('street_number', { required: required ? 'Number is required' : false })}
            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-shadow ${
              errors?.street_number ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="e.g. 70"
          />
          {errors?.street_number && (
            <p className="mt-1 text-sm text-red-500">{errors.street_number.message as string}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {/* Postal Code */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            Postal Code {required && <span className="text-red-500">*</span>}
          </label>
          <input
            type="text"
            {...register('postal_code', { 
              required: required ? 'Postal code is required' : false,
              minLength: { value: 4, message: 'Postal code must be at least 4 digits' }
            })}
            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-shadow ${
              errors?.postal_code ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="e.g. 10243"
            maxLength={10}
          />
          {errors?.postal_code && (
            <p className="mt-1 text-sm text-red-500">{errors.postal_code.message as string}</p>
          )}
        </div>

        {/* City */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            City {required && <span className="text-red-500">*</span>}
          </label>
          <input
            type="text"
            {...register('city', { required: required ? 'City is required' : false })}
            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-shadow ${
              errors?.city ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="e.g. Berlin"
          />
          {errors?.city && (
            <p className="mt-1 text-sm text-red-500">{errors.city.message as string}</p>
          )}
        </div>
      </div>

      {/* Country */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
          Country
        </label>
        <select
          {...register('country')}
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-shadow"
        >
          <option value="Germany">Germany</option>
          <option value="Austria">Austria</option>
          <option value="Switzerland">Switzerland</option>
          <option value="Netherlands">Netherlands</option>
          <option value="Poland">Poland</option>
          <option value="Czech Republic">Czech Republic</option>
        </select>
      </div>

      <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-xs text-blue-700">
          <strong>💡 Tip:</strong> The address will be automatically geo-referenced for accurate map positioning.
        </p>
      </div>
    </div>
  );
};

